/** Cliente de la API. El token de sesión vive en localStorage; cada llamada lo envía como Bearer. */
let token = localStorage.getItem('sesion') ?? '';

export function guardarToken(t: string) { token = t; localStorage.setItem('sesion', t); }
export function cerrarSesion() { token = ''; localStorage.removeItem('sesion'); }
export function haySesion() { return !!token; }

export class ErrorApi extends Error { constructor(public estado: number, mensaje: string) { super(mensaje); } }

export async function api<T = unknown>(ruta: string, opciones: { metodo?: string; cuerpo?: unknown; form?: FormData } = {}): Promise<T> {
  const r = await fetch(ruta, {
    method: opciones.metodo ?? (opciones.cuerpo || opciones.form ? 'POST' : 'GET'),
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opciones.cuerpo ? { 'Content-Type': 'application/json' } : {}) },
    body: opciones.form ?? (opciones.cuerpo ? JSON.stringify(opciones.cuerpo) : undefined),
  });
  if (r.status === 401 && !ruta.startsWith('/api/sesion')) { cerrarSesion(); location.reload(); }
  const datos = r.headers.get('content-type')?.includes('json') ? await r.json() : await r.text();
  if (!r.ok) throw new ErrorApi(r.status, (datos as { error?: string })?.error ?? `Error ${r.status}`);
  return datos as T;
}

/**
 * Abre (PDF) o descarga (XML) un archivo de la API enviando el token: un enlace directo no lleva el
 * encabezado Authorization y la API respondería 401. La ventana se abre antes del fetch para que
 * Safari no la bloquee como ventana emergente.
 */
export async function abrirArchivo(ruta: string, nombre?: string): Promise<void> {
  const ventana = nombre ? null : window.open('', '_blank');
  try {
    const r = await fetch(ruta, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!r.ok) {
      let m = `Error ${r.status}`;
      try { m = ((await r.json()) as { error?: string }).error ?? m; } catch { /* sin cuerpo JSON */ }
      throw new Error(m);
    }
    const url = URL.createObjectURL(await r.blob());
    setTimeout(() => URL.revokeObjectURL(url), 120_000);
    if (ventana) { ventana.location.href = url; return; }
    const a = document.createElement('a');
    a.href = url;
    a.download = nombre ?? ruta.split('/').pop() ?? 'archivo';
    document.body.appendChild(a); a.click(); a.remove();
  } catch (e) {
    ventana?.close();
    throw e;
  }
}

export const clp = (n: number) => '$ ' + Math.round(n).toLocaleString('es-CL');
export const fmtRut = (r: string) => { const [c, d] = r.split('-'); return c.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + '-' + d; };
export const fmtFecha = (f: string) => f.slice(0, 10).split('-').reverse().join('-');

export interface Emisor { id: string; rut: string; razon_social: string; giro: string; segmento: string; modulos: string[]; ambiente: string; estado_certificacion: string; forma_rebaja: 'A' | 'B' }
export interface Cliente { id: string; rut: string; razon_social: string; giro: string | null; direccion: string | null; comuna: string | null; tipo: string }
export interface Sesion { usuarioId: string; correo: string; nombre: string; rol: 'administrador' | 'emisor' | 'lectura'; emisores: string[]; sin2fa?: boolean }
export interface DocumentoLista { id: string; tipo_dte: number; tipo_nombre: string; folio: number | null; formato: string; fecha_emision: string; estado: string; monto_total: number; glosa_resumen: string | null; cliente_rut: string | null; cliente_nombre: string | null; con_pdf: boolean; track_id_sii: string | null }
export interface Folio { tipo: number; disponibles: number; siguiente: number | null }
export interface Firmante { id: string; rut: string; nombre: string; certificado_vence_en: string | null; con_certificado: boolean }

/** Tipos de documento que cada emisor puede emitir, con el formato que implica cada entrada (estilo menú del SII). */
export interface TipoEmision { clave: string; dte: number; nombre: string; formato: 'simple' | 'hibrida' | 'ceec' | 'arriendo' }
export function tiposDe(e: Emisor): TipoEmision[] {
  const t: TipoEmision[] = [];
  const m = e.modulos;
  if (m.includes('ceec')) t.push({ clave: '33c', dte: 33, nombre: 'Factura electrónica con crédito de empresas constructoras', formato: 'ceec' });
  if (m.includes('inmo')) t.push({ clave: '33h', dte: 33, nombre: 'Factura electrónica afecta y exenta (híbrida)', formato: 'hibrida' });
  if (m.includes('boletas')) t.push({ clave: '39', dte: 39, nombre: 'Boleta electrónica', formato: 'simple' }, { clave: '41', dte: 41, nombre: 'Boleta exenta electrónica', formato: 'simple' });
  if (m.includes('arriendos')) t.push({ clave: '33a', dte: 33, nombre: 'Factura electrónica · arriendo amoblado (deducción 11%)', formato: 'arriendo' });
  t.push({ clave: '33', dte: 33, nombre: 'Factura electrónica', formato: 'simple' }, { clave: '34', dte: 34, nombre: 'Factura no afecta o exenta electrónica', formato: 'simple' });
  if (m.includes('admin')) t.push({ clave: '43', dte: 43, nombre: 'Liquidación factura electrónica', formato: 'simple' }, { clave: '46', dte: 46, nombre: 'Factura de compra electrónica', formato: 'simple' });
  t.push({ clave: '61', dte: 61, nombre: 'Nota de crédito electrónica', formato: 'simple' }, { clave: '56', dte: 56, nombre: 'Nota de débito electrónica', formato: 'simple' });
  if (e.segmento === 'servicios_recurrentes') { const i = t.findIndex((x) => x.clave === '34'); t.unshift(...t.splice(i, 1)); }
  return t;
}
